import React, { useCallback, useMemo } from 'react';
import { addEdge, type Connection } from '@xyflow/react';

import type { WorkflowEdge } from 'model/xyflowMapping';

export interface UseCanvasConnectionsArgs {
  /**
   * Edge state setter, owned by xyflow's `useEdgesState` in the canvas.
   * The hook mutates the edges list directly so consecutive drops between
   * the same two nodes don't pile up duplicate entries.
   */
  setEdges: React.Dispatch<React.SetStateAction<WorkflowEdge[]>>;
  /**
   * Parent callback fired when a per-field dataflow handle (`out:field` →
   * `in:field`) is connected. Used to update the workflow's
   * `InputBinding` map via the parent's undo stack.
   */
  onConnectInputBinding?: (
    targetId: string,
    fieldName: string,
    sourceId: string,
    outputName: string,
  ) => void;
}

export interface UseCanvasConnectionsResult {
  /** Drop handler that decides between control-flow and dataflow connection. */
  handleConnect: (connection: Connection) => void;
  /** Validator passed straight to ReactFlow's `isValidConnection` prop. */
  isValidConnection: (connection: Connection | WorkflowEdge) => boolean;
}

/**
 * Extract the label after the `:` in a handle id (e.g. `out:foo` → `foo`).
 * Plain control handles (`out`, `in`) have no `:` and return `undefined`.
 */
const getHandleLabel = (handleId?: string | null): string | undefined => {
  if (!handleId) return undefined;
  const separatorIndex = handleId.indexOf(':');
  return separatorIndex >= 0 ? handleId.substring(separatorIndex + 1) : undefined;
};

/**
 * Encapsulates the canvas's connection rules. Lifted out of
 * `WorkflowCanvas` so the control-edge vs data-edge logic can be tested
 * independently. Behaviour is preserved verbatim from the previous
 * inline implementation — see the canvas's git history for the
 * earlier shape.
 */
export const useCanvasConnections = ({
  setEdges,
  onConnectInputBinding,
}: UseCanvasConnectionsArgs): UseCanvasConnectionsResult => {
  const isValidConnection = useCallback(
    (connection: Connection | WorkflowEdge) => connection.source !== connection.target,
    [],
  );

  const handleConnect = useCallback(
    (connection: Connection) => {
      const sourceOutputName = getHandleLabel(connection.sourceHandle ?? undefined);
      const targetInputName = getHandleLabel(connection.targetHandle ?? undefined);

      // Dataflow connection: both handles carry a field label AND both endpoints
      // resolve to real nodes. The explicit truthy checks (no non-null assertions)
      // let TypeScript narrow `sourceOutputName` / `targetInputName` to `string`
      // for the rest of the block.
      if (sourceOutputName && targetInputName && connection.source && connection.target) {
        // Dataflow connection: tell the parent to set the input binding (so it
        // goes through the undo stack), then also create a runAfter edge so the
        // backend's input-reachability validation passes.
        const sourceId = connection.source;
        const targetId = connection.target;
        const inputName = targetInputName;
        const outputName = sourceOutputName;

        onConnectInputBinding?.(targetId, inputName, sourceId, outputName);

        setEdges((eds) => {
          const dataEdgeId = `data:${sourceId}->${targetId}:${inputName}`;
          const companionControlEdgeId = `control:${sourceId}->${targetId}`;
          const next = eds.filter((e) => e.id !== dataEdgeId);
          next.push({
            id: dataEdgeId,
            source: sourceId,
            target: targetId,
            sourceHandle: `out:${outputName}`,
            targetHandle: `in:${inputName}`,
            label: inputName,
            data: { kind: 'data' },
          });
          if (!next.some((e) => e.id === companionControlEdgeId)) {
            next.push({
              id: companionControlEdgeId,
              source: sourceId,
              target: targetId,
              sourceHandle: 'out',
              targetHandle: 'in',
              style: { strokeWidth: 1.5 },
              interactionWidth: 20,
              data: { kind: 'control', enableOnCondition: false },
            });
          }

          return next;
        });
        return;
      }

      // Control-flow connection (runAfter). Stable id + dedupe so consecutive
      // drops between the same two nodes don't pile up duplicate runAfter
      // entries when xyflowToWorkflow serializes the graph.
      if (!connection.source || !connection.target) return;
      const label = targetInputName ?? sourceOutputName;
      const controlEdgeId = `control:${connection.source}->${connection.target}`;
      setEdges((eds) => {
        if (eds.some((e) => e.id === controlEdgeId)) return eds;
        return addEdge(
          {
            ...connection,
            id: controlEdgeId,
            ...(label && { label }),
            style: { strokeWidth: 1.5 },
            interactionWidth: 20,
            data: { kind: 'control', enableOnCondition: false },
          },
          eds,
        );
      });
    },
    [setEdges, onConnectInputBinding],
  );

  // Memo-wrap so callers that pass the whole result object to a memoised
  // child get a stable reference across re-renders without state changes.
  return useMemo(() => ({ handleConnect, isValidConnection }), [handleConnect, isValidConnection]);
};
